import {
  Avatar,
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spacer,
  Text,
  useDisclosure,
  useToast,
  IconButton,
  HStack,
  Badge,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import {
  Menu as MenuIcon,
  Bell,
  UserPlus,
  LogOut,
  Settings,
  HelpCircle,
  ChevronDown,
} from "lucide-react";
import profile from "../icons/user.jpg";
import bell from "../icons/bell.jpeg";
import Menus from "../utils/Menus";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { postData } from "../redux/actions/common.action";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import axiosInstance from "../utils/axiosInterceptor";
import {
  NOTIFICATIONS_MARK_ALL_READ_LOCAL,
  NOTIFICATIONS_MARK_READ_LOCAL,
  NOTIFICATIONS_SET_UNREAD_COUNT,
} from "../redux/types/common.types";

interface SigninUserDetails {
  signinuser: {
    Name: string;
    Email: string;
    menuPermissions: [string];
    UserType: number;
    ComapanyImageOne: string;
    ComapanyImageTwo?: string;
    image?: string;
    adminProfileAvatarSource?: "user" | "company1" | "company2";
    userId?: string;
  };
  notificationBellItems?: Array<{
    _id: string;
    message: string;
    isRead?: boolean;
    entityType?: string;
    entityId?: string;
    createdAt?: string;
  }>;
  notificationUnreadCount?: number;
}

interface NavbarProps {
  onOpenSidebar: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenSidebar }) => {
  const dispatch: any = useDispatch();
  const toast = useToast();
  const endpoint: string = "auth/signout";
  const navigate = useNavigate();

  const { signinuser, notificationBellItems, notificationUnreadCount } =
    useSelector((state: { common: SigninUserDetails }) => state.common);

  const unread = notificationUnreadCount ?? 0;
  const bellItems = notificationBellItems ?? [];

  const notificationTargetPath = (n: {
    entityType?: string;
    entityId?: string;
  }) => {
    if (n.entityType === "lead") return "/lead-list";
    if (n.entityType === "todo") {
      return signinuser?.UserType === 2 ? "/admin-dashboard" : "/";
    }
    return "/notifications";
  };

  const markNotificationRead = async (id: string) => {
    try {
      const res = await axiosInstance.put(`notifications/${id}/read`);
      dispatch({ type: NOTIFICATIONS_MARK_READ_LOCAL, payload: { id } });
      if (typeof res.data?.unreadCount === "number") {
        dispatch({
          type: NOTIFICATIONS_SET_UNREAD_COUNT,
          payload: { count: res.data.unreadCount },
        });
      }
    } catch {
      /* ignore */
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axiosInstance.put("notifications/read-all");
      dispatch({ type: NOTIFICATIONS_MARK_ALL_READ_LOCAL });
      dispatch({ type: NOTIFICATIONS_SET_UNREAD_COUNT, payload: { count: 0 } });
      toast({ title: "All notifications marked read", status: "success", duration: 2000 });
    } catch {
      toast({
        title: "Could not mark all read",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openNotification = async (n: {
    _id: string;
    isRead?: boolean;
    entityType?: string;
    entityId?: string;
  }) => {
    if (!n.isRead) await markNotificationRead(n._id);
    navigate(notificationTargetPath(n));
  };

  const { isOpen, onOpen, onClose } = useDisclosure();

  const avatarSrc = (() => {
    if (!signinuser) return undefined;
    if (signinuser.UserType === 3) return signinuser.image;
    if (signinuser.UserType === 2) {
      const pref = String(signinuser.adminProfileAvatarSource || "user");
      if (pref === "company1") return signinuser.ComapanyImageOne;
      if (pref === "company2") return signinuser.ComapanyImageTwo;
      return signinuser.image;
    }
    // super admin fallback
    return signinuser.image || signinuser.ComapanyImageOne;
  })();

  const handleLogout = () => {
    dispatch(postData({ type: "web" }, endpoint, navigate, toast));
    Cookies.remove("token");
    navigate("/login");
  };

  return (
    <>
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }
          
          @keyframes shimmer {
            0% {
              background-position: -200% center;
            }
            100% {
              background-position: 200% center;
            }
          }
          
          .navbar-animate {
            animation: slideDown 0.5s ease-out;
          }
          
          .notification-pulse {
            animation: pulse 2s infinite;
          }
          
          .gradient-btn {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            background-size: 200% 200%;
            animation: shimmer 3s linear infinite;
          }
        `}
      </style>

      <Box
        className="navbar-animate"
        position="fixed"
        top="0"
        left="0"
        right="0"
        bg="white"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
        zIndex={999}
        borderBottom="1px solid"
        borderColor="gray.100"
        backdropFilter="blur(10px)"
      >
        <Flex
          align="center"
          px={{ base: 3, md: 6 }}
          py={{ base: 2.5, md: 3 }}
          maxW="100vw"
        >
          {/* Modern Menu Toggle Button */}
          <Box
            as="button"
            onClick={onOpenSidebar}
            position="relative"
            mr={3}
            p={2}
            borderRadius="xl"
            bg="gradient-to-br"
            bgGradient="linear(to-br, blue.50, purple.50)"
            border="1px solid"
            borderColor="blue.200"
            _hover={{
              bgGradient: "linear(to-br, blue.100, purple.100)",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 16px rgba(59, 130, 246, 0.2)",
            }}
            _active={{
              transform: "translateY(0)",
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <MenuIcon size={20} color="#4F46E5" />
          </Box>

          {/* Page Title with Icon */}
          <Menus />

          <Spacer />

          {/* Action Buttons Container */}
          <HStack spacing={{ base: 2, md: 3 }}>
            {/* Modern Add Lead Button */}
            {signinuser?.UserType !== 1 &&
              signinuser?.menuPermissions?.includes("Lead List") && (
                <Button
                  onClick={() => navigate("/add-lead")}
                  size={{ base: "sm", md: "md" }}
                  position="relative"
                  overflow="hidden"
                  bgGradient="linear(135deg, #10b981 0%, #059669 50%, #34d399 100%)"
                  color="white"
                  borderRadius="full"
                  leftIcon={<UserPlus size={16} />}
                  fontWeight="600"
                  px={{ base: 3, md: 6 }}
                  display={{ base: "none", sm: "flex" }}
                  border="1px solid"
                  borderColor="whiteAlpha.400"
                  boxShadow="0 4px 20px rgba(16, 185, 129, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                    transition: "left 0.5s ease",
                  }}
                  _hover={{
                    bgGradient:
                      "linear(135deg, #059669 0%, #10b981 50%, #6ee7b7 100%)",
                    transform: "translateY(-2px)",
                    boxShadow:
                      "0 8px 30px rgba(5, 150, 105, 0.6), 0 0 20px rgba(52, 211, 153, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                    borderColor: "whiteAlpha.600",
                    _before: {
                      left: "100%",
                    },
                  }}
                  _active={{
                    transform: "translateY(0) scale(0.98)",
                    boxShadow: "0 2px 10px rgba(16, 185, 129, 0.4)",
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  backdropFilter="blur(10px)"
                >
                  <Text display={{ base: "none", md: "block" }}>Add Lead</Text>
                </Button>
              )}

            {/* Mobile Add Lead Button */}
            {signinuser?.UserType !== 1 &&
              signinuser?.menuPermissions?.includes("Lead List") && (
                <IconButton
                  aria-label="Add Lead"
                  icon={<UserPlus size={18} />}
                  onClick={() => navigate("/add-lead")}
                  size="sm"
                  display={{ base: "flex", sm: "none" }}
                  bgGradient="linear(to-r, blue.500, purple.600)"
                  color="white"
                  borderRadius="full"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.4)",
                  }}
                />
              )}

            {/* Modern Notifications */}
            <Popover placement="bottom-end">
              <PopoverTrigger>
                <Box position="relative" cursor="pointer">
                  <Box
                    p={2}
                    borderRadius="xl"
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{
                      bg: "blue.50",
                      borderColor: "blue.300",
                      transform: "translateY(-2px)",
                    }}
                    transition="all 0.2s"
                  >
                    <Bell size={20} color="#6B7280" />
                  </Box>
                  {unread > 0 ? (
                    <Box
                      className="notification-pulse"
                      position="absolute"
                      top="0"
                      right="0"
                      minW="18px"
                      h="18px"
                      px={1}
                      bg="red.500"
                      borderRadius="full"
                      border="2px solid white"
                      boxShadow="0 0 0 2px rgba(239, 68, 68, 0.2)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="10px" fontWeight="700" color="white">
                        {unread > 99 ? "99+" : unread}
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              </PopoverTrigger>
              <PopoverContent
                w="360px"
                maxW="400px"
                mx={4}
                outline="none"
                borderRadius="2xl"
                boxShadow="0 20px 40px rgba(0, 0, 0, 0.15)"
                border="1px solid"
                borderColor="gray.100"
              >
                <PopoverArrow />
                <PopoverHeader
                  fontWeight="700"
                  fontSize="md"
                  borderTopRadius="2xl"
                  bg="gradient-to-r"
                  bgGradient="linear(to-r, blue.500, purple.600)"
                  color="white"
                  py={3}
                >
                  <HStack justify="space-between" flexWrap="wrap" gap={2}>
                    <HStack>
                      <Text>Notifications</Text>
                      <Badge
                        colorScheme="red"
                        borderRadius="full"
                        px={2}
                        fontSize="xs"
                      >
                        {unread}
                      </Badge>
                    </HStack>
                    <Button
                      size="xs"
                      variant="solid"
                      colorScheme="whiteAlpha"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAllNotificationsRead();
                      }}
                      isDisabled={unread === 0}
                    >
                      Mark all read
                    </Button>
                  </HStack>
                </PopoverHeader>
                <Box maxH="320px" overflowY="auto">
                  {bellItems.length === 0 ? (
                    <Box p={6} textAlign="center">
                      <VStack spacing={2}>
                        <Bell size={48} color="#CBD5E0" />
                        <Text fontSize="sm" color="gray.500" fontWeight="500">
                          No notifications yet
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          Assignments and task updates appear here
                        </Text>
                      </VStack>
                    </Box>
                  ) : (
                    <VStack spacing={0} align="stretch">
                      {bellItems.map((n) => (
                        <Box
                          key={n._id}
                          px={4}
                          py={3}
                          cursor="pointer"
                          borderBottomWidth="1px"
                          borderColor="gray.100"
                          bg={n.isRead ? "white" : "blue.50"}
                          _hover={{ bg: "gray.50" }}
                          onClick={() => openNotification(n)}
                        >
                          <Text
                            fontSize="sm"
                            fontWeight={n.isRead ? "normal" : "semibold"}
                            noOfLines={3}
                          >
                            {n.message}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>
                <Box px={4} py={3} borderTopWidth="1px" borderColor="gray.100">
                  <Button
                    as={Link}
                    to="/notifications"
                    variant="link"
                    colorScheme="blue"
                    size="sm"
                    w="full"
                  >
                    View all notifications
                  </Button>
                </Box>
              </PopoverContent>
            </Popover>

            {/* Modern User Profile Dropdown */}
            <Popover
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <HStack
                  cursor="pointer"
                  spacing={2}
                  p={1}
                  pr={3}
                  borderRadius="full"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _hover={{
                    bg: "blue.50",
                    borderColor: "blue.300",
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.2s"
                >
                  <Avatar
                    size="sm"
                    name={signinuser?.Name}
                    src={avatarSrc}
                    border="2px solid"
                    borderColor="blue.400"
                  />
                  <ChevronDown
                    size={16}
                    color="#6B7280"
                    style={{
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s",
                    }}
                  />
                </HStack>
              </PopoverTrigger>
              <PopoverContent
                w="280px"
                maxW="320px"
                mx={4}
                outline="none"
                borderRadius="2xl"
                boxShadow="0 20px 40px rgba(0, 0, 0, 0.15)"
                border="1px solid"
                borderColor="gray.100"
                overflow="hidden"
              >
                <PopoverArrow />

                {/* Profile Header */}
                <PopoverHeader
                  borderTopRadius="2xl"
                  border="none"
                  p={4}
                  bg="gradient-to-r"
                  bgGradient="linear(to-r, blue.500, purple.600)"
                >
                  <Link to="/profile" onClick={onClose}>
                    <HStack spacing={3} cursor="pointer">
                      <Avatar
                        src={avatarSrc}
                        size="md"
                        border="2px solid white"
                        boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
                      />
                      <VStack align="start" spacing={0} flex="1">
                        <Text
                          fontWeight="700"
                          fontSize="md"
                          color="white"
                          noOfLines={1}
                        >
                          {signinuser?.Name}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="whiteAlpha.900"
                          noOfLines={1}
                        >
                          {signinuser?.Email}
                        </Text>
                      </VStack>
                    </HStack>
                  </Link>
                </PopoverHeader>

                {/* Menu Items */}
                <VStack spacing={0} py={2} align="stretch">
                  <Link to="/profile" onClick={onClose}>
                    <Flex
                      px={4}
                      py={3}
                      alignItems="center"
                      gap={3}
                      cursor="pointer"
                      _hover={{
                        bg: "blue.50",
                      }}
                      transition="all 0.2s"
                    >
                      <Box
                        p={2}
                        bg="blue.100"
                        borderRadius="lg"
                        color="blue.600"
                      >
                        <Settings size={16} />
                      </Box>
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        Profile Settings
                      </Text>
                    </Flex>
                  </Link>

                  <Link
                    to={`${
                      signinuser?.UserType === 1 ? "/super-support" : "/support"
                    }`}
                    onClick={onClose}
                  >
                    <Flex
                      px={4}
                      py={3}
                      alignItems="center"
                      gap={3}
                      cursor="pointer"
                      _hover={{
                        bg: "purple.50",
                      }}
                      transition="all 0.2s"
                    >
                      <Box
                        p={2}
                        bg="purple.100"
                        borderRadius="lg"
                        color="purple.600"
                      >
                        <HelpCircle size={16} />
                      </Box>
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        Help & Support
                      </Text>
                    </Flex>
                  </Link>

                  <Box h="1px" bg="gray.100" my={1} />

                  <Flex
                    onClick={handleLogout}
                    px={4}
                    py={3}
                    alignItems="center"
                    gap={3}
                    cursor="pointer"
                    _hover={{
                      bg: "red.50",
                    }}
                    transition="all 0.2s"
                  >
                    <Box p={2} bg="red.100" borderRadius="lg" color="red.600">
                      <LogOut size={16} />
                    </Box>
                    <Text fontSize="sm" fontWeight="700" color="red.600">
                      Logout
                    </Text>
                  </Flex>
                </VStack>
              </PopoverContent>
            </Popover>
          </HStack>
        </Flex>
      </Box>
    </>
  );
};

export default Navbar;
